import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, ExternalLink, ArrowDownToLine, X } from 'lucide-react';
import { dettaglioAPI, anamnesiVociAPI } from '../../services/api';
import { useAuthStore } from '../../store/useStore';
import { annunciaAnamnesiCambiata } from '../../utils/annunci';

/**
 * ContributiProcedure — quello che i medici hanno scritto nelle
 * procedure (note anamnestiche, esame obiettivo, finalita'
 * terapeutiche), visto dalla scheda Anamnesi.
 *
 * LA PROCEDURA NON SCRIVE DENTRO L'ANAMNESI. Mai. L'anamnesi e' il
 * registro longitudinale del paziente, la procedura una fotografia di
 * un momento: se la seconda sovrascrivesse la prima non si saprebbe piu'
 * chi ha scritto cosa, e correggere una procedura riscriverebbe il
 * passato del paziente. Quindi qui si VEDE - in ordine di data, con
 * l'autore e il rimando alla procedura - in SOLA LETTURA: si modifica
 * dove e' nato, e la voce qui segue perche' e' derivata, non copiata.
 *
 * «Porta nell'anamnesi» e' l'atto esplicito del medico: crea una voce
 * di anamnesi a suo nome, con la data di oggi, nella sezione che
 * sceglie. Mai automatico.
 *
 * La riservatezza la applica il server: un contributo di una procedura
 * riservata non arriva a chi non puo' vederla.
 */
const dataIta = (v) => (v ? new Date(String(v).slice(0, 10)).toLocaleDateString('it-IT') : '');

/* «Patologie in atto» non c'e' piu' dal 22 settembre 2026: la stessa
   domanda clinica stava in tre posti, e le 14 voci gia' scritte restano
   in archivio ma non si mostrano e non se ne scrivono di nuove. Le
   condizioni attuali si scrivono in «Patologie pregresse» o in «Note». */
const SEZIONI = [
    ['patologie_remote', 'Patologie pregresse'],
    ['allergie', 'Allergie'],
    ['farmaci', 'Terapie in corso'],
    ['familiare', 'Anamnesi familiare'],
    ['note', 'Note'],
];

const ContributiProcedure = ({ pazienteId, onPortata }) => {
    const utente = useAuthStore((s) => s.user);
    const navigate = useNavigate();
    const [contributi, setContributi] = useState([]);
    const [errore, setErrore] = useState(null);
    /* { chiave, sezione } del contributo che si sta portando */
    const [porta, setPorta] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            const r = await dettaglioAPI.contributi(pazienteId);
            setContributi(r.data?.data?.contributi || []);
            setErrore(null);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Non riesco a leggere i contributi delle procedure');
        }
    }, [pazienteId]);

    useEffect(() => { carica(); }, [carica]);

    const chiave = (c) => `${c.visita_id}:${c.campo}`;

    const conferma = async (c) => {
        setInCorso(true);
        try {
            await anamnesiVociAPI.crea(pazienteId, { sezione: porta.sezione, testo: c.testo });
            setPorta(null);
            annunciaAnamnesiCambiata();
            if (onPortata) onPortata();
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Errore nel salvataggio della voce');
        } finally {
            setInCorso(false);
        }
    };

    if (!contributi.length && !errore) return null;

    return (
        <section className="cp-riquadro">
            <h3 className="cp-titolo">
                <Stethoscope size={16} strokeWidth={1.8} aria-hidden="true" /> Dalle procedure
                <span className="cp-conta">{contributi.length}</span>
            </h3>
            <p className="cp-spiega">
                Quello che è stato scritto durante le procedure, con la data e il medico
                di quella procedura. <b>Qui si legge e basta</b>: si corregge nella
                procedura, e la voce segue. Per farne parte dell'anamnesi corrente usa
                «Porta nell'anamnesi»: nasce una voce a tuo nome.
            </p>
            {errore && <p className="av-errore">{errore}</p>}
            <ul className="cp-elenco">
                {contributi.map((c) => (
                    <li key={chiave(c)} className="cp-voce">
                        <div className="cp-testa">
                            <span className="cp-campo">{c.titolo}</span>
                            <span className="cp-meta">
                                {dataIta(c.data)}{c.medico ? ` · Dr. ${c.medico}` : ''}
                                {c.trattamento ? ` · ${c.trattamento}` : ''}
                                {c.riservata ? ' · riservata' : ''}
                            </span>
                        </div>
                        <p className="cp-testo">{c.testo}</p>
                        <div className="cp-azioni">
                            <button type="button" className="rm-btn"
                                    onClick={() => navigate(`/pazienti/${pazienteId}`,
                                        { state: { schermata: 'procedure', visitaId: c.visita_id } })}>
                                <ExternalLink size={13} /> Apri la procedura
                            </button>
                            {utente?.medico_id && porta?.chiave !== chiave(c) && (
                                <button type="button" className="rm-btn"
                                        onClick={() => setPorta({ chiave: chiave(c), sezione: 'patologie_prossime' })}>
                                    <ArrowDownToLine size={13} /> Porta nell'anamnesi
                                </button>
                            )}
                        </div>
                        {porta?.chiave === chiave(c) && (
                            <div className="cp-porta">
                                <label>
                                    <span>In quale sezione</span>
                                    <select value={porta.sezione}
                                            onChange={(e) => setPorta({ ...porta, sezione: e.target.value })}>
                                        {SEZIONI.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                </label>
                                <button type="button" className="rm-btn rm-btn-primario" disabled={inCorso}
                                        onClick={() => conferma(c)}>
                                    {inCorso ? 'Salvo…' : 'Crea la voce a mio nome'}
                                </button>
                                <button type="button" className="rm-btn" onClick={() => setPorta(null)}>
                                    <X size={13} /> Annulla
                                </button>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default ContributiProcedure;
